import React, { FC, memo, useEffect, useRef, useReducer, useState, ReactElement } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { throttle } from 'lodash';
import VirtualList from 'react-tiny-virtual-list-dmk';
import type { ALIGNMENT } from 'react-tiny-virtual-list-dmk/types/constants';
import { isMobile, isTablet } from 'react-device-detect';
import { usePrevious } from 'react-use';
import type { List as ImList, Set as ImSet, OrderedMap } from 'immutable';

import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

import type RootState from 'companion-app-components/utils/redux-store/rootState';
import type { CashFlowTransaction, TransactionProps } from 'companion-app-components/flux/transactions/transactionsTypes';
import { datasetsSelectors } from 'companion-app-components/flux/datasets';
import { authSelectors } from 'companion-app-components/flux/auth';
import { accountsSelectors, accountsUtils } from 'companion-app-components/flux/accounts';
import { getLogger } from 'companion-app-components/utils/core';
import { featureFlagsSelectors } from 'companion-app-components/flux/feature-flags';

import { transactionListMetrics as txnListMetrics } from 'data/transactionList/selectors';
import * as transactionSelectors from 'data/transactions/selectors';
import { isExistingTxn } from 'data/transactions/utils';
import PanelUpIcon from 'assets/icon-arrow-right.inline.svg';
import transactionsIcon from 'assets/nav-menu/accounts.svg';
import RegisterHeader from 'components/TransactionRegister/RegisterHeader';
import BulkEditPanel from 'components/BulkEditPanel';
import DocumentBrowser from 'components/DocumentBrowser';
import store from 'store';
import {
  RegisterContainer, styles, MIN_TX_HEIGHT,
} from './styles';
import TransactionRow from '../TransactionRow2';
import TxnPageFooter from './TxnPageFooter';

const log = getLogger('components/TransactionPage/index.js');
const useStyles = makeStyles()(styles as Record<string, any>);

const REDISPLAY_DELAY = 5;

type TransactionPageState = {
  displayNodes: Record<string, any>[];
  displayHeights: Record<string, any>;
  scrollIndex: null | number;
  bodyOffset: number;
  parent: null | HTMLElement;
};

interface TransactionPageProps {
  uiState: Record<string, any>;
  sortBy: string;
  sortOrder: string;
  wrapperId: string;
  adapt: number;
  calendarDate: object;

  // Specified
  callbacks: Record<string, any>;
  scrollToId: string;
  currEditFieldName: string;
  hideDividers: boolean;
  hideSearch: boolean;
  zeroStateMessage: string | null;
  zeroStateStyle: Record<string, any>;
  reminderSetting: string | number;
  regFieldsOrderChange: () => void;

  // From Parent
  accountIds: ImList<string>;  // Precisely, an immutable OrderedMap
  acctTxns: OrderedMap<string, ImList<TransactionProps>>;
  showControls: boolean;
  noNew: boolean;
  tightMargins: boolean;
  sizeChanged: boolean;
  hideFooter: boolean;
  hideColumnsHeader: boolean;
  calendar: boolean;
  splitsInDialog: boolean;
  allAccountIds: ImSet<string>;

  // PREFS
  doubleAmounts: boolean;
  editDownloaded: boolean;
  deleteDownloaded: boolean;
  showAttachmentColumn: boolean;
  showSplitColumn: boolean;
  showReviewedColumn: boolean;
  showSelectColumn: boolean;
  longCats: boolean;
  showCurrencySymbol: boolean;
  registerComfort: string;
  headerFields: ImList<string>;
  wrapText: boolean;
  showAccountColors: boolean;
  sizeToContent: boolean;

  // DEBT NOT SUPPORTED IN QUICKEN WEB
  showDebtErrorMessage: boolean;
}

const TransactionPage: FC<TransactionPageProps> = (props) => {
  const { classes, theme }: { classes: Record<string, any>, theme: Record<string, any> } = useStyles();
  const accounts = useSelector(accountsSelectors.getAccountsById);
  const transactionListMetrics = useSelector((state: RootState) =>
    txnListMetrics(state, props.acctTxns));
  const loadPending = useSelector(transactionSelectors.getLoadPending);
  const featureFlags = useSelector(featureFlagsSelectors.getFeatureFlags);
  const hideLoanTransactions = featureFlags.get('hideLoanTransactions');
  const datasetId = useSelector(authSelectors.getDatasetId);
  const datasetsById = useSelector(datasetsSelectors.getDatasetsById);
  const [, forceUpdate] = useReducer((val) => val + 1, 0);

  const [state, setState] = useState<TransactionPageState>({
    displayNodes: [],
    displayHeights: {},
    scrollIndex: null,
    bodyOffset: 0,
    parent: null,
  });

  let numUpdates: number = 0;
  let recalcWindow = Date.now();
  let footerElHeight;
  let headerElHeight;
  const headerEl = useRef<null | HTMLElement>(null);
  const footerEl = useRef<null | HTMLElement>(null);
  const virtualList = useRef<null | VirtualList>(null);
  const scrollIndexRef = useRef<number | null>(null);
  const zeroStateMessage = props.zeroStateMessage || "You don't have any transactions in this account yet";

  const makeId = (row, acct) =>
    `txRow:${props.accountIds.hashCode()}${row}:${acct}`;

  const getItemSize = (index) => {
    const node = state.displayNodes[index];

    if (node && node.node) {
      const el = document.getElementById(node.node.props.id);
      if (el) {
        return el.offsetHeight;
      }
      const { id } = node.node.props;
      return state.displayHeights[id] ? state.displayHeights[id] : node.height;
    }
    return 0;
  };

  const rowInnerRef = (elem: HTMLElement | null) => {
    if (elem && elem.id) {
      state.displayHeights[elem.id] = elem.offsetHeight;
      if (elem.className.indexOf('appear') < 0) {
        // eslint-disable-next-line no-param-reassign
        elem.className = `${elem.className} appear`;
      }
    }
  };

  const regComponentRef = (ref, elem) => {
    // eslint-disable-next-line no-param-reassign
    ref.current = elem;
    if (footerEl.current && headerEl.current) {
      footerElHeight = footerEl.current.offsetHeight;
      headerElHeight = headerEl.current.offsetHeight;
      state.bodyOffset = footerElHeight + headerElHeight;
      if (!state.parent) {
        forceUpdate();
      }
      state.parent = state.parent || document.getElementById(props.wrapperId);
    }
  };

  const recalcHeights = (newState) => {
    let needUpdate = false;
    let lastNodeRect: null | DOMRect = null;
    let nodeMap = state.displayNodes.map((dnode) => {
      if (dnode.node && dnode.node.props) {
        return document.getElementById(dnode.node.props.id); // this.state.domNodes[dnode.node.props.id]);
      }
      return null;
    });
    nodeMap = nodeMap.filter((node) => node && node.offsetHeight !== 0);
    nodeMap.sort((node1, node2) => {
      if (node1 && node2) {
        const rectA = node1.getBoundingClientRect();
        const rectB = node2.getBoundingClientRect();
        return (rectA.top - rectB.top);
      }
      return 0;
    });
    nodeMap.forEach((node, index) => {
      if (node) {
        const actualHeight = node.offsetHeight;
        const compareHeight = state.displayHeights[node.id];
        const nodeRect = node.getBoundingClientRect();
        const nodeHeight = nodeRect.height.toFixed(0);
        const heightFromLastNode = lastNodeRect ? (nodeRect.bottom - lastNodeRect.bottom).toFixed(0) : nodeHeight;
        if (nodeHeight !== heightFromLastNode) {
          log.log('Height MISMATCH ', index, nodeHeight, heightFromLastNode, compareHeight);
        }

        if ((actualHeight && compareHeight && (actualHeight !== compareHeight)) || (nodeHeight !== heightFromLastNode)) {
          // if ((actualHeight && compareHeight && (actualHeight !== compareHeight))) {
          needUpdate = true;
          // We purposefully do not use setState to ensure we don't trigger uneeded updates
          state.displayHeights[node.id] = actualHeight;
        }
        lastNodeRect = nodeRect;
      }

      return true;
    });

    if (virtualList.current) {
      virtualList.current.recomputeSizes(0);
    }

    // establish recalc windows of 1 seconds in length
    if (Date.now().valueOf() - recalcWindow.valueOf() > 1000) {
      recalcWindow = Date.now();
      numUpdates = 0;
    }

    // only re-update up to 5 times per recalc window
    if (needUpdate && numUpdates < 5) {
      setState((prevState) => ({
        ...prevState,
        ...newState,
        scrollIndex: scrollIndexRef.current,
      }));
      numUpdates += 1;
      return false;
    }
    return true;
  };

  const updateSize = () => {
    numUpdates = 0;
    let resizeTimer;
    if (resizeTimer) {
      clearTimeout(resizeTimer);
    }
    resizeTimer = setTimeout(() => forceUpdate(), 100);
  };

  const onSizeChange = () => {
    numUpdates = 0;
    setTimeout(recalcHeights, 250);
  };

  const onScroll = () => {
    if (!props.wrapText || !props.longCats) { return; }
    setTimeout(() => throttle(recalcHeights, 500), REDISPLAY_DELAY);
  };

  //-------------------------------------------------------
  // Render Transaction Edit Row
  //
  const renderTransactionEditRow = (
    callbacks,
    txn,
    editable,
    id,
    refFn,
    scriptId,
    addQcardID = false,
    _shade = false,
    lastRow = false,
    showBalance = true,
  ) => {
    const { regEditableFields, flashTx } = props.uiState;
    const doFlash = flashTx && (flashTx === txn.clientId || flashTx === txn.id);

    // do not show transactions for loan accounts
    if (!hideLoanTransactions
      || !accountsUtils.isLoanAccount(accountsSelectors.getAccountById(store.getState(), txn.accountId))) {

      return (
        <TransactionRow
          key={`txnPage:${txn.id}`}
          qcardId={addQcardID ? 'qCard-transaction-first-row' : undefined}
          accountIds={props.accountIds}
          refFn={refFn}
          sizesChanged={onSizeChange}
          id={id}
          callbacks={callbacks}
          txn={txn}
          editable={Boolean(editable)}
          lastRow={Boolean(lastRow)}
          onRowSelect={callbacks.onRowSelect}
          showAttachmentColumn={Boolean(props.showAttachmentColumn && props.uiState.showAttachmentColumnInHeader)}
          sortOrder={props.sortOrder}
          sortBy={props.sortBy}
          currEditFieldName={editable ? props.currEditFieldName : null}
          accounts={accounts}
          regEditableFields={regEditableFields}
          simpleEdit={editable ? props.uiState.simpleEdit && !props.splitsInDialog : false}
          selectedTxns={props.uiState.selectedTxns}
          showBalance={Boolean(showBalance)}
          doubleAmounts={props.doubleAmounts}
          deleteDownloaded={props.deleteDownloaded}
          editDownloaded={props.editDownloaded}
          showSplitWindow={editable ? Boolean(props.uiState.showSplitWindow && !props.splitsInDialog) : false}
          showSplitColumn={props.showSplitColumn}
          showSelectColumn={props.showSelectColumn}
          showReviewedColumn={props.showReviewedColumn}
          showAccountColors={props.showAccountColors}
          longCats={props.longCats}
          flashTx={Boolean(doFlash)}
          onFocusField={callbacks.onFocusField}
          showCurrencySymbol={props.showCurrencySymbol}
          showAccounts={props.accountIds.size !== 1}
          registerComfort={props.registerComfort}
          headerFields={props.headerFields}
          wrapText={props.wrapText}
          scriptId={`${props.wrapperId}-${String(scriptId)}`}
          wrapperId={props.wrapperId}
          calendar={props.calendar}
          allAccountIds={props.allAccountIds}
          isTxnDirty={props.uiState.txnDirty}
          showNewTxn={props.uiState.showNewTxn}
        />
      );
    }
    return null;
  };

  //-------------------------------------------------------
  // Render all transactions
  //
  // This function populates this.state.displayNodes
  // and this.state.displayHeights.  displayNodes are an array of
  // REACT node types for rendering, displayHeights is an array of objects
  // containing the actual DOM references as well as their offsetHeight's
  //
  // These arrays can be used to manage virtual scrolling
  //
  const renderTxList = (callbacks, accountIds, acctTxns, showBalance) => {
    const { uiState } = props;
    let editable = false;
    state.displayNodes = [];

    let acctKey = 'acctKey';
    acctKey = accountIds.forEach((v) => `${acctKey}:${v}`);

    acctTxns.keySeq().forEach((key, indexMonth) => {
      let shade = false;
      const keyPrefix = `TxSep:${acctKey}`;
      const shortKey = key.slice(key.indexOf(':') + 1);
      const sectionOpen = uiState.openSections.has(shortKey);
      //----------------------------------
      // push the section header
      //----------------------------------
      let count = state.displayNodes.length;
      let node: ReactElement | null = (
        <div
          id={`${makeId(shortKey, acctKey)}:${sectionOpen ? 'OPEN' : 'CLOSED'}`}
          ref={(elemDiv) => rowInnerRef(elemDiv)}
          key={`${keyPrefix}${shortKey}`}
        >
          <MenuItem
            disableGutters
            className={classes.txnListMenuItem}
          >
            <Paper
              elevation={0}
              className={classNames(classes.txSepSummary)}
              onClick={() => callbacks.openCloseSection(shortKey)}
            >
              <IconButton className={classes.txSepIcon} size="large">
                <PanelUpIcon
                  width={15}
                  height={15}
                  fill={theme.components?.payeeField.displayTemplate}
                  className={sectionOpen ? classes.turnDown90 : classes.turnUp}
                />
              </IconButton>
              <Typography variant="subtitle1" className={classes.txSepHeading}>
                {shortKey}
              </Typography>
            </Paper>
          </MenuItem>
        </div>
      );

      if (!props.hideDividers && acctTxns.get(key).size) {
        state.displayNodes.push({ node, height: Number(theme.components?.register.sectionHeaderHeight) + 1 });
      }

      if (sectionOpen || props.hideDividers) {
        const acctTxnsSet = new Set(acctTxns.get(key));
        acctTxnsSet.forEach((txn: CashFlowTransaction, indexTxn) => {
          //----------------------------------
          // push the transactions in this section
          //----------------------------------
          editable = (uiState.txnBeingEdited && uiState.txnBeingEdited.id === txn.id);
          const txnToRender = editable ? uiState.txnBeingEdited : txn;

          count = state.displayNodes.length;

          node = renderTransactionEditRow(
            callbacks,
            txnToRender,
            editable,
            makeId(txnToRender.id, acctKey),
            rowInnerRef,
            count,
            (indexMonth === 0 && indexTxn === 0), // Show qCardId on the first txn only
            shade,
            false,
            showBalance,
          );
          state.displayNodes.push({ node, height: MIN_TX_HEIGHT[props.registerComfort] });

          if (props.scrollToId && (String(props.scrollToId) === String(txn.id))) {
            scrollIndexRef.current = state.displayNodes.length - 1;
          }
          shade = !shade;
        });
      }
    });

    // render virtual row
    let virtualRow;
    if (uiState.showNewTxn) {
      if (uiState.txnBeingEdited && !isExistingTxn(uiState.txnBeingEdited)) {
        virtualRow = renderTransactionEditRow(
          callbacks,
          uiState.txnBeingEdited,
          true,
          makeId(uiState.txnBeingEdited.clientId || 'lastrow', acctKey),
          rowInnerRef,
          'new',
          false,
          true,
          true,
          showBalance,
        );
      }

      if (props.sortOrder === 'ascending') {
        state.displayNodes.push({ node: virtualRow, height: MIN_TX_HEIGHT[props.registerComfort] });
        if (String(props.scrollToId) === String(uiState.txnBeingEdited && uiState.txnBeingEdited.id)) {
          scrollIndexRef.current = state.displayNodes.length - 1;
        }
      } else {
        state.displayNodes.unshift({ node: virtualRow, height: MIN_TX_HEIGHT[props.registerComfort] });
        if (String(props.scrollToId) === String(uiState.txnBeingEdited && uiState.txnBeingEdited.id)) {
          scrollIndexRef.current = 0;
        }
      }
    }
  };

  const hideEdit = () => props.callbacks.hideModal('bulkEdit');

  const zeroStateContainer = (
    // zeroStateStyle injected prop @ ProjectedBalancesCalendar
    <div className={classes.zeroStateContainer}>
      {!props.zeroStateStyle &&
        <img
          alt="No Transactions"
          src={transactionsIcon}
          className={classes.imgZeroState}
        />}
      <br />
      <div
        className={classes.zeroStateTextBody}
        style={props.zeroStateStyle && { ...props.zeroStateStyle }}
      >
        {loadPending ? 'Transactions Loading...' : zeroStateMessage
          || "You don't have any transactions in this account yet"}
      </div>
    </div>
  );

  useEffect(() => {
    window.addEventListener('resize', updateSize);
    setTimeout(() => {
      document
        .getElementById('txn-register-virtual-list')
        ?.addEventListener?.('scroll', onScroll);
    }, 7000);
    setTimeout(recalcHeights, 1000);

    return () => {
      window.removeEventListener('resize', updateSize);
      document
        .getElementById('txn-register-virtual-list')
        ?.removeEventListener?.('scroll', onScroll);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const adaptPrev = usePrevious(props.adapt);
  const uiStatePrev = usePrevious(props.uiState);
  useEffect(() => {
    if (adaptPrev !== props.adapt) {
      setTimeout(() => {
        forceUpdate();
      }, 0);
    }
    if (uiStatePrev !== props.uiState) {
      setTimeout(recalcHeights, REDISPLAY_DELAY);
    }
    setState((prevState) => ({
      ...prevState,
      scrollIndex: null,
    }));
    scrollIndexRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adaptPrev, props.adapt, uiStatePrev, props.uiState]);

  useEffect(() => {
    if (state.scrollIndex !== null) {
      props.callbacks.scrollToTransaction(null);
      setState((prevState) => ({
        ...prevState,
        scrollIndex: null,
      }));
      scrollIndexRef.current = null;
    }
    if (!isMobile && !isTablet) {
      setTimeout(recalcHeights, REDISPLAY_DELAY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.scrollIndex, props.callbacks]);

  const showBalance = props.sortBy === 'date' && props.accountIds.size === 1;
  const scrollIndex = state.displayNodes.length > Number(state.scrollIndex) ? state.scrollIndex : null;

  if (props.acctTxns) {
    renderTxList(props.callbacks, props.accountIds, props.acctTxns, showBalance);
  }

  // identify dataset type
  const dataset = datasetId ? datasetsById.get(datasetId) : null;

  return (
    <>
      {props.uiState.showBulkEdit && (
        <BulkEditPanel
          txnArray={props.uiState.selectedTxns}
          inDialog
          autoFocus
          closeFn={hideEdit}
          saveFn={props.callbacks.bulkEditSave}
          longCats={props.longCats}
        />
      )}

      {props.uiState.showDocumentBrowserTxn &&
        <DocumentBrowser
          onClose={() => props.callbacks.hideModal('documentBrowser')}
          documents={props.uiState.showDocumentBrowserTxn.attachments}
          selectedIds={props.callbacks.delDocIds}
        />}

      <RegisterContainer
        className={classNames('register-container', { tightmargins: props.tightMargins })}
        onKeyDown={(e) => props.callbacks.regFieldKey(e)}
      >
        <RegisterHeader
          innerRegRef={(elem) => { regComponentRef(headerEl, elem); }}
          callbacks={props.callbacks}
          uiState={props.uiState}
          showBalance={showBalance}
          accountIds={props.accountIds}
          showControls={props.showControls}
          headerFields={props.headerFields}
          doubleAmounts={props.doubleAmounts}
          showAccountColors={props.showAccountColors}
          acctTxns={props.acctTxns}
          noNew={props.noNew}
          sizeChanged={props.sizeChanged}
          hideSearch={props.hideSearch}
          hideColumnsHeader={props.hideColumnsHeader}
          transactionListMetrics={transactionListMetrics}
          reminderSetting={props.reminderSetting}
          sortBy={props.sortBy}
          sortOrder={props.sortOrder}
          draggableFields={Boolean(props.regFieldsOrderChange)}
          dataset={dataset}
          showDebtErrorMessage={props.showDebtErrorMessage}
          calendarDate={props.calendarDate}
        />
        {props.acctTxns.size || props.uiState.showNewTxn ?
          <>
            {props.sizeToContent &&
              <div>
                {state.displayNodes.map((item) => item.node)}
              </div>}

            {!props.sizeToContent &&
              <VirtualList
                height={(state.parent ? state.parent.clientHeight : state.bodyOffset) - state.bodyOffset}
                itemCount={state.displayNodes.length}
                overscanCount={8}
                itemSize={getItemSize}
                scrollToIndex={scrollIndex !== null ? scrollIndex : undefined}
                scrollToAlignment={'start' as ALIGNMENT}
                ref={(elem) => { virtualList.current = elem; }}
                renderItem={({ index, style }) => (
                  <div
                    key={index}
                    style={{ ...style, height: 'auto' }}
                  >
                    {state.displayNodes[index] ? state.displayNodes[index].node : ''}
                  </div>)}
                className={classes.VL}
                // @ts-expect-error: FIXME - Property 'id' does not exist 
                id="txn-register-virtual-list"
              />}
          </>
          : zeroStateContainer}
        <TxnPageFooter
          footerRef={(elem) => { regComponentRef(footerEl, elem); }}
          hideFooter={props.hideFooter}
          hideSearch={props.hideSearch}
          classes={classes}
          transactionListMetrics={transactionListMetrics}
          theme={theme}
        />
      </RegisterContainer>
    </>
  );
};

export default memo(TransactionPage);
